import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import axios from 'axios';
import { loginSuccess } from '../store/slices/authSlice';
import { consumePostAuthRedirect } from '../utils/postAuthRedirect';
import { getApiBaseUrl } from '../utils/apiUrls';
import { consumeLegacyOAuthCallback } from '../utils/legacyOAuthCallback';
import { useI18n } from '../i18n';

const API_BASE_URL = getApiBaseUrl();

const AuthCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discordBotLinked, setDiscordBotLinked] = useState(false);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const {token,errorMessage,fromDiscordBot}=consumeLegacyOAuthCallback(
          searchParams,window.history,window.location.pathname
        );

        if (errorMessage) {
          try {
            setError(decodeURIComponent(errorMessage));
          } catch {
            setError(errorMessage);
          }
          setLoading(false);
          return;
        }

        if (!token) {
          setError(t('authCallback.errorNoToken'));
          setLoading(false);
          return;
        }

        const statusRes = await axios.get(`${API_BASE_URL}/auth/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const u = statusRes.data?.user;
        if (!u) {
          setError(t('authCallback.errorUser'));
          setLoading(false);
          return;
        }

        dispatch(loginSuccess({ token, user: u }));


        if (fromDiscordBot) {
          setDiscordBotLinked(true);
          setLoading(false);
          return;
        }

        const next = consumePostAuthRedirect('/');
        setTimeout(() => {
          navigate(next, { replace: true });
        }, 900);

      } catch (err) {
        setError(t('authCallback.errorGeneric'));
        setLoading(false);
      }
    };

    handleAuthCallback();
  }, [searchParams, dispatch, navigate, t]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('authCallback.processing')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          {t('authCallback.home')}
        </Button>
      </Box>
    );
  }

  if (discordBotLinked) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          p: 3,
        }}
      >
        <Alert severity="success" sx={{ mb: 2, maxWidth: 480 }}>
          {t('authCallback.discordLinkedTitle')}
        </Alert>
        <Typography variant="body1" sx={{ mb: 1, textAlign: 'center', maxWidth: 480 }}>
          {t('authCallback.discordLinkedBody')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('authCallback.discordLinkedClose')}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          {t('authCallback.discordLinkedWeb')}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
      }}
    >
      <Alert severity="success" sx={{ mb: 2, maxWidth: 400 }}>
        {t('authCallback.success')}
      </Alert>
      <Typography variant="body2" color="text.secondary">
        {t('authCallback.successRedirect')}
      </Typography>
    </Box>
  );
};

export default AuthCallback;
